import React, { useState, useEffect, useRef, Suspense } from 'react';
import { useLocation, useNavigate, Routes, Route } from 'react-router-dom';
import axios from 'axios';
import Admin from './Admin';
import Partners from './Partners';
import JobApplication from './JobApplication';
import ResetPassword from './ResetPassword';
import CookieConsent from './CookieConsent';
import PrivacyPolicy from './PrivacyPolicy';
import TermsOfService from './TermsOfService';
import Disclaimer from './Disclaimer';
import './App.css';
import './i18n';
import { useTranslation } from 'react-i18next';

function AppContent() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Set active tab based on current route
  const getActiveTab = () => {
    const path = location.pathname;
    if (path === '/admin') return 'admin';
    if (path === '/partners') return 'partners';
    if (path === '/about/work') return 'work'; // Separate route for work page
    if (path === '/about') return 'about';
    if (path === '/projects') return 'projects';
    if (path === '/gallery') return 'gallery';
    if (path === '/calculator') return 'calculator';
    if (path === '/blog') return 'blog';
    if (path === '/contact') return 'contact';
    return 'home';
  };

  const [activeTab, setActiveTab] = useState(getActiveTab());
  
  // Update activeTab when route changes
  useEffect(() => {
    const path = location.pathname;
    if (path === '/admin') setActiveTab('admin');
    else if (path === '/partners') setActiveTab('partners');
    else if (path === '/about/work') setActiveTab('work'); // Separate route for work page
    else if (path === '/about') setActiveTab('about');
    else if (path === '/projects') setActiveTab('projects');
    else if (path === '/gallery') setActiveTab('gallery');
    else if (path === '/calculator') setActiveTab('calculator');
    else if (path === '/blog') setActiveTab('blog');
    else if (path === '/contact') setActiveTab('contact');
    else setActiveTab('home');
    
    // Close job application modal when leaving work page
    if (path !== '/about/work') {
      setSelectedJob(null);
    }
  }, [location.pathname]);

  // State declarations
  const [companyInfo, setCompanyInfo] = useState({});
  const [aboutContent, setAboutContent] = useState({
    title: 'About Our Company',
    description: '',
    mission: '',
    vision: '',
    services: []
  });
  const [projectStatus, setProjectStatus] = useState([]);
  const [projects, setProjects] = useState([]);
  const [files, setFiles] = useState([]);
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [contactSuccess, setContactSuccess] = useState(false);
  const [contactError, setContactError] = useState('');
  const [quoteForm, setQuoteForm] = useState({ 
    name: '', email: '', phone: '', projectType: '', budget: '', description: '' 
  });
  const [quoteSuccess, setQuoteSuccess] = useState(false);
  const [quoteError, setQuoteError] = useState('');
  const [socialLinks, setSocialLinks] = useState({
    facebook: '', instagram: '', tiktok: '', linkedin: '', youtube: '', twitter: ''
  });
  const [activeContactTab, setActiveContactTab] = useState('contact');
  const [lightboxImage, setLightboxImage] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [calculatorData, setCalculatorData] = useState({
    serviceId: '', area: ''
  });
  const [estimatedPrice, setEstimatedPrice] = useState(0);
  const [calculatorServices, setCalculatorServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [beforeAfterProjects, setBeforeAfterProjects] = useState([]);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const messagesEndRef = useRef(null);
  const [counters, setCounters] = useState({ experience: 0, projects: 0, clients: 0 });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobApplicationForm, setJobApplicationForm] = useState({
    name: '', email: '', phone: '', message: '', cv: null, coverLetter: null
  });
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);

  // Language switching with react-i18next
  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };
  
  // Safe language getter
  const currentLanguage = i18n?.language || 'en';

  // Define fetch functions after state declarations
  const fetchCompanyInfo = async () => {
    try {
      const response = await axios.get('/api/company');
      setCompanyInfo(response.data);
    } catch (error) {
      console.error('Error fetching company info:', error);
    }
  };

  const fetchSocialLinks = async () => {
    try {
      const response = await axios.get('/api/social-links');
      setSocialLinks(response.data);
    } catch (error) {
      console.error('Error fetching social links:', error);
    }
  };

  const fetchProjectStatus = async () => {
    try {
      const response = await axios.get('/api/status');
      setProjectStatus(response.data);
    } catch (error) {
      console.error('Error fetching project status:', error);
    }
  };

  const fetchBeforeAfterProjects = async () => {
    try {
      console.log('Fetching before/after projects...');
      const response = await axios.get('/api/before-after');
      console.log('Before/After response:', response.data);
      if (Array.isArray(response.data) && response.data.length > 0) {
        setBeforeAfterProjects(response.data);
      }
    } catch (error) {
      console.error('Error fetching before/after projects:', error);
      // Don't set to empty array on error - keep existing data
    }
  };

  const fetchAboutContent = async () => {
    try {
      const response = await axios.get('/api/about');
      setAboutContent(response.data);
    } catch (error) {
      console.error('Error fetching about content:', error);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get('/api/projects');
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchFiles = async () => {
    try {
      const response = await axios.get('/api/files');
      setFiles(response.data);
    } catch (error) {
      console.error('Error fetching files:', error);
    }
  };

  const fetchCalculatorServices = async () => {
    try {
      const response = await axios.get('/api/calculator-services');
      setCalculatorServices(response.data);
    } catch (error) {
      console.error('Error fetching calculator services:', error);
    }
  };

  const fetchJobs = async () => {
    try {
      const response = await axios.get('/api/jobs');
      setJobs(response.data.filter(job => job.status === 'active'));
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
  };

  // Fetch data on component mount and when returning to home
  useEffect(() => {
    const fetchData = async () => {
      await fetchCompanyInfo();
      await fetchSocialLinks();
      await fetchProjectStatus();
      await fetchFiles();
      await fetchBeforeAfterProjects();
      await fetchAboutContent();
      await fetchProjects();
      await fetchCalculatorServices();
      await fetchJobs();
    };
    fetchData();
  }, [location.pathname]);

  // Auto-scroll chat to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Track page views
  useEffect(() => {
    // Send page view to server for analytics (only if consent given)
    const trackPageView = async () => {
      const consent = localStorage.getItem('cookieConsent');
      if (consent !== 'accepted') return; // Don't track if not accepted
      
      try {
        await axios.post('/api/track-view', { page: location.pathname });
      } catch (error) {
        // Silently fail - don't disrupt user experience
      }
    };
    trackPageView();
  }, [location.pathname]);

  // Navigation function that updates URL
  const navigateToTab = (tab) => {
    setActiveTab(tab);
    const path = tab === 'home' ? '/' : `/${tab}`;
    navigate(path);
    
    // Refresh before/after projects when gallery is opened
    if (tab === 'gallery') {
      fetchBeforeAfterProjects();
    }
    
    // Re-trigger scroll animations when returning to home
    if (tab === 'home') {
      setTimeout(() => {
        const elements = document.querySelectorAll('.animate-on-scroll');
        elements.forEach(el => {
          el.classList.remove('animate-on-scroll');
          void el.offsetHeight; // Trigger reflow
          el.classList.add('animate-on-scroll');
        });
      }, 100);
    }
  };
  
  // All useEffect hooks must be called before any early returns
  // Initialize chat with welcome message when language changes
  useEffect(() => {
    setChatMessages([
      { id: 1, text: t('chatWelcome') || 'Welcome! How can I help you?', sender: 'bot', time: new Date() }
    ]);
  }, [t]);

  // Animated counter effect
  useEffect(() => {
    const animateCounters = () => {
      const targets = { experience: 15, projects: 150, clients: 200 };
      const duration = 2000;
      const steps = 60;
      const stepTime = duration / steps;
      let currentStep = 0;
      
      const timer = setInterval(() => {
        currentStep++;
        const progress = currentStep / steps;
        
        setCounters({
          experience: Math.floor(targets.experience * progress),
          projects: Math.floor(targets.projects * progress),
          clients: Math.floor(targets.clients * progress)
        });
        
        if (currentStep >= steps) {
          clearInterval(timer);
          setCounters(targets);
        }
      }, stepTime);
    };
    
    const timeout = setTimeout(animateCounters, 500);
    return () => clearTimeout(timeout);
  }, []);

  // Scroll animation observer
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, observerOptions);

    const elements = document.querySelectorAll('.animate-on-scroll');
    
    // Reset animation state when on home page
    if (activeTab === 'home') {
      elements.forEach(el => {
        el.classList.remove('animate-in');
      });
    }
    
    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, [activeTab]);

  // Check for existing token on load
  useEffect(() => {
    const token = localStorage.getItem('token') || localStorage.getItem('adminToken');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setIsAdminLoggedIn(true);
    }
  }, []);

  // Refresh before/after projects when category changes
  useEffect(() => {
    if (selectedCategory === 'before-after') {
      fetchBeforeAfterProjects();
    }
  }, [selectedCategory]);

  // Handler functions
  const handleJobApplicationSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('jobId', selectedJob.id);
      formData.append('name', jobApplicationForm.name);
      formData.append('email', jobApplicationForm.email);
      formData.append('phone', jobApplicationForm.phone);
      formData.append('message', jobApplicationForm.message);
      
      if (jobApplicationForm.cv) {
        formData.append('cv', jobApplicationForm.cv);
      }
      if (jobApplicationForm.coverLetter) {
        formData.append('coverLetter', jobApplicationForm.coverLetter);
      }
      
      await axios.post('/api/job-applications', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      alert('Application submitted successfully!');
      setJobApplicationForm({ name: '', email: '', phone: '', message: '', cv: null, coverLetter: null });
      setSelectedJob(null);
    } catch (error) {
      alert(error.response?.data?.error || 'Error submitting application');
    }
  };

  const deleteBeforeAfterProject = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    
    try {
      await axios.delete(`/api/before-after/${id}`);
      alert('Project deleted successfully!');
      await fetchBeforeAfterProjects();
      setCurrentSlide(0); // Reset to first slide
    } catch (error) {
      console.error('Error deleting project:', error);
      alert('Error deleting project');
    }
  };

  // Wait for i18n to initialize (after all hooks)
  if (!i18n) {
    return <div>Loading...</div>;
  }

  // Check current route AFTER all hooks
  const currentPath = location.pathname;
  console.log('Current path:', currentPath);
  
  // Route to different components AFTER hooks
  if (currentPath === '/admin') {
    return <Admin />;
  }
  
  if (currentPath === '/partners') {
    return <Partners />;
  }

  if (currentPath === '/about/work') {
    return (
      <div className="work-with-us-page">
        <header className="site-header">
          <div className="header-content">
            <h1 onClick={() => navigate('/')} style={{cursor: 'pointer'}}>
              {companyInfo.name || 'Gjenerali Construction'}
            </h1>
            <nav className="main-nav">
              <button onClick={() => navigate('/')}>Home</button>
              <button onClick={() => navigate('/about')}>About</button>
              <button onClick={() => navigate('/contact')}>Contact</button>
            </nav>
          </div>
        </header>

        <main className="page-content">
          <section className="jobs-page-section">
            <h1>💼 Work With Us</h1>
            <p className="page-subtitle">Join our team and be part of exciting construction projects</p>
            
            {jobs.length > 0 ? (
              <div className="jobs-grid">
                {jobs.map(job => (
                  <div key={job.id} className="job-card">
                    <h2>{job.title}</h2>
                    <div className="job-meta">
                      <span className="job-type-badge">{job.type}</span>
                      {job.location && <span className="job-location">📍 {job.location}</span>}
                      {job.salary && <span className="job-salary">💰 {job.salary}</span>}
                    </div>
                    
                    <div className="job-description">
                      <h3>Description</h3>
                      <p>{job.description}</p>
                    </div>
                    
                    {job.requirements && (
                      <div className="job-requirements">
                        <h3>Requirements</h3>
                        <p>{job.requirements}</p>
                      </div>
                    )}
                    
                    <button 
                      className="apply-button"
                      onClick={() => setSelectedJob(job)}
                    >
                      Apply Now
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-jobs">
                <p>No open positions at the moment. Check back soon!</p>
              </div>
            )}
          </section>
        </main>

        <footer className="site-footer">
          <p>&copy; 2026 {companyInfo.name || 'Gjenerali Construction'}. All rights reserved.</p>
        </footer>

        {/* Job Application Modal */}
        {selectedJob && (
          <div className="modal-overlay" onClick={() => setSelectedJob(null)}>
            <div className="modal-content job-modal" onClick={(e) => e.stopPropagation()}>
              <button className="modal-close" onClick={() => setSelectedJob(null)}>×</button>
              
              <div className="job-details">
                <h2>{selectedJob.title}</h2>
                <div className="job-meta">
                  <span className="job-type">{selectedJob.type}</span>
                  {selectedJob.location && <span>📍 {selectedJob.location}</span>}
                  {selectedJob.salary && <span>💰 {selectedJob.salary}</span>}
                </div>
                
                <div className="job-section">
                  <h3>Description</h3>
                  <p>{selectedJob.description}</p>
                </div>
                
                {selectedJob.requirements && (
                  <div className="job-section">
                    <h3>Requirements</h3>
                    <p>{selectedJob.requirements}</p>
                  </div>
                )}
                
                <div className="job-section">
                  <h3>Apply for this Position</h3>
                  <form onSubmit={handleJobApplicationSubmit} className="job-application-form">
                    <input
                      type="text"
                      placeholder="Your Name *"
                      value={jobApplicationForm.name}
                      onChange={(e) => setJobApplicationForm({...jobApplicationForm, name: e.target.value})}
                      required
                    />
                    
                    <input
                      type="email"
                      placeholder="Your Email *"
                      value={jobApplicationForm.email}
                      onChange={(e) => setJobApplicationForm({...jobApplicationForm, email: e.target.value})}
                      required
                    />
                    
                    <input
                      type="tel"
                      placeholder="Your Phone"
                      value={jobApplicationForm.phone}
                      onChange={(e) => setJobApplicationForm({...jobApplicationForm, phone: e.target.value})}
                    />
                    
                    <textarea
                      placeholder="Cover Letter / Message"
                      value={jobApplicationForm.message}
                      onChange={(e) => setJobApplicationForm({...jobApplicationForm, message: e.target.value})}
                      rows="4"
                    />
                    
                    <div className="file-upload-group">
                      <label>
                        📄 Upload CV (optional)
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={(e) => setJobApplicationForm({...jobApplicationForm, cv: e.target.files[0]})}
                        />
                      </label>
                      
                      <label>
                        📝 Upload Cover Letter (optional)
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={(e) => setJobApplicationForm({...jobApplicationForm, coverLetter: e.target.files[0]})}
                        />
                      </label>
                    </div>
                    
                    <button type="submit" className="submit-application-btn">
                      Submit Application
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Continue with main website component
  const calculatePrice = () => {
    const { serviceId, area } = calculatorData;
    
    if (!serviceId || !area) {
      alert('Please select a service and enter the area');
      return;
    }
    
    const service = calculatorServices.find(s => s.id === parseInt(serviceId));
    if (!service) {
      alert('Service not found');
      return;
    }
    
    const total = parseInt(area) * service.pricePerM2;
    setEstimatedPrice(total);
    setSelectedService(service);
  };

  const testimonials = currentLanguage === 'sq' ? [
    { id: 1, name: "Altin Hoxha", text: "Punë e shkëlqyer në shtëpinë tonë familjare. Profesional dhe në kohë!", rating: 5 },
    { id: 2, name: "Ana Krasniqi", text: "Rinovim i mahnitshëm i hapësirës sonë të zyrës. Shumë e rekomanduar!", rating: 5 },
    { id: 3, name: "Gjergj Leka", text: "Punë ndërtimi cilësore. Shumë i kënaqur me rezultatet.", rating: 5 }
  ] : [
    { id: 1, name: "Altin Hoxha", text: "Excellent work on our family home. Professional and on time!", rating: 5 },
    { id: 2, name: "Ana Krasniqi", text: "Amazing renovation of our office space. Highly recommended!", rating: 5 },
    { id: 3, name: "Gjergj Leka", text: "Quality construction work. Very satisfied with the results.", rating: 5 }
  ];

  // Touch gesture handlers for mobile swipe
  const handleTouchStart = (e) => {
    setTouchEnd(0);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && currentSlide < portfolioImages.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
    if (isRightSwipe && currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  // Portfolio images for slider
  const portfolioImages = [
    { id: 1, title: "Modern Villa - Durres", category: "residential", before: "🏚️", after: "🏡" },
    { id: 2, title: "Office Complex - Tirana", category: "commercial", before: "🏢", after: "🏬" },
    { id: 3, title: "Restaurant Renovation", category: "commercial", before: "🏪", after: "🍽️" },
    { id: 4, title: "Family Home Extension", category: "residential", before: "🏠", after: "🏘️" }
  ];

  const openWhatsApp = () => {
    const message = encodeURIComponent("Hello! I'm interested in your construction services.");
    window.open(`https://wa.me/355682091156?text=${message}`, '_blank');
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactSuccess(false);
    setContactError('');
    try {
      const response = await axios.post('/api/contact', contactForm);
      setContactSuccess(true);
      setContactForm({ name: '', email: '', message: '' });
      setTimeout(() => setContactSuccess(false), 5000); // Hide after 5 seconds
    } catch (error) {
      console.error('Contact form error:', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.errors?.[0]?.msg || 'Error sending message. Please try again.';
      setContactError(errorMsg);
      setTimeout(() => setContactError(''), 5000);
    }
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setQuoteSuccess(false);
    setQuoteError('');
    try {
      const response = await axios.post('/api/quote', quoteForm);
      setQuoteSuccess(true);
      setQuoteForm({ name: '', email: '', phone: '', projectType: '', budget: '', description: '' });
      setTimeout(() => setQuoteSuccess(false), 5000);
    } catch (error) {
      console.error('Quote form error:', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.errors?.[0]?.msg || 'Error sending quote request. Please try again.';
      setQuoteError(errorMsg);
      setTimeout(() => setQuoteError(''), 5000);
    }
  };

  const getBotResponse = (message) => {
    const msg = message.toLowerCase();
    
    // Greeting responses
    if (msg.includes('hello') || msg.includes('hi') || msg.includes('hey') || 
        msg.includes('përshëndetje') || msg.includes('tungjatjeta')) {
      return {
        text: currentLanguage === 'en' ? 
          "Hello! Great to meet you! 😊 I'm here to help with all your construction needs. Are you planning a new project or looking to renovate?" :
          "Përshëndetje! Gëzohem që ju takova! 😊 Jam këtu për t'ju ndihmuar me të gjitha nevojat tuaja të ndërtimit. A po planifikoni një projekt të ri apo dëshironi të rinovoni?"
      };
    }
    
    if (msg.includes('price') || msg.includes('cost') || msg.includes('quote') || msg.includes('estimate') || 
        msg.includes('çmim') || msg.includes('kosto') || msg.includes('ofertë')) {
      return {
        text: currentLanguage === 'en' ? 
          "Great question! 💰 Every project is unique, so our pricing depends on size, materials, and complexity. Let me help you get the information you need:" :
          "Pyetje e shkëlqyer! 💰 Çdo projekt është unik, kështu që çmimet tona varen nga madhësia, materialet dhe kompleksiteti. Le t'ju ndihmoj të merrni informacionin që ju nevojitet:",
        actions: [
          { text: currentLanguage === 'en' ? "📊 Use Price Calculator" : "📊 Përdorni Kalkulatorin", action: "calculator" },
          { text: currentLanguage === 'en' ? "📞 Get Free Quote" : "📞 Merrni Ofertë Falas", action: "contact" },
          { text: currentLanguage === 'en' ? "📧 Email Us" : "📧 Na Shkruani", action: "email" }
        ]
      };
    }
    
    if (msg.includes('service') || msg.includes('what do you do') || msg.includes('construction') ||
        msg.includes('shërbim') || msg.includes('çfarë bëni') || msg.includes('ndërtim')) {
      return {
        text: currentLanguage === 'en' ? 
          "We're Gjenerali SHPK Construction, serving Durres, Albania for 15+ years! Our services include:\n\n🏠 Residential Construction\n🏢 Commercial Buildings\n🔨 Renovations & Remodeling\n📋 Project Management\n\nWhich service interests you most?" :
          "Ne jemi Gjenerali SHPK Construction, duke shërbyer në Durrës, Shqipëri për 15+ vjet! Shërbimet tona përfshijnë:\n\n🏠 Ndërtim Rezidencial\n🏢 Ndërtesa Komerciale\n🔨 Rinovim & Rimodelim\n📋 Menaxhim Projektesh\n\nCili shërbim ju intereson më shumë?",
        actions: [
          { text: currentLanguage === 'en' ? "🏠 Residential Projects" : "🏠 Projekte Rezidenciale", action: "residential" },
          { text: currentLanguage === 'en' ? "🏢 Commercial Projects" : "🏢 Projekte Komerciale", action: "commercial" },
          { text: currentLanguage === 'en' ? "📸 View Our Gallery" : "📸 Shikoni Galerinë", action: "gallery" }
        ]
      };
    }
    
    if (msg.includes('contact') || msg.includes('reach') || msg.includes('talk') || msg.includes('speak') ||
        msg.includes('kontakt') || msg.includes('telefononi') || msg.includes('flisni')) {
      return {
        text: currentLanguage === 'en' ? 
          "Ready to discuss your project? Here are the best ways to reach our team:" :
          "Gati të diskutoni projektin tuaj? Këto janë mënyrat më të mira për të kontaktuar ekipin tonë:",
        actions: [
          { text: `📞 ${currentLanguage === 'en' ? 'Call' : 'Telefononi'} ${companyInfo.phone}`, action: "call" },
          { text: `📧 Email ${companyInfo.email}`, action: "email" },
          { text: currentLanguage === 'en' ? "📍 Visit Our Office" : "📍 Vizitoni Zyrën", action: "address" },
          { text: currentLanguage === 'en' ? "💬 WhatsApp Us" : "💬 WhatsApp", action: "whatsapp" }
        ]
      };
    }
    
    if (msg.includes('time') || msg.includes('how long') || msg.includes('duration') ||
        msg.includes('kohë') || msg.includes('sa kohë') || msg.includes('kohëzgjatja')) {
      return {
        text: currentLanguage === 'en' ? 
          "Project timelines depend on scope and complexity:\n\n🏠 Residential: 3-6 months\n🏢 Commercial: 6-12 months\n🔨 Renovations: 1-4 months\n\nFor accurate timeline estimates:" :
          "Kohëzgjatja e projekteve varet nga përmbajtja dhe kompleksiteti:\n\n🏠 Rezidencial: 3-6 muaj\n🏢 Komercial: 6-12 muaj\n🔨 Rinovime: 1-4 muaj\n\nPër vlerësime të sakta të kohës:",
        actions: [
          { text: currentLanguage === 'en' ? "📞 Schedule Consultation" : "📞 Planifikoni Konsultim", action: "contact" },
          { text: currentLanguage === 'en' ? "📊 Use Calculator" : "📊 Përdorni Kalkulatorin", action: "calculator" },
          { text: currentLanguage === 'en' ? "📧 Send Project Details" : "📧 Dërgoni Detajet", action: "email" }
        ]
      };
    }
    
    if (msg.includes('hello') || msg.includes('hi') || msg.includes('hey') || msg.includes('good') ||
        msg.includes('përshëndetje') || msg.includes('tungjatjeta') || msg.includes('mirëdita')) {
      return {
        text: currentLanguage === 'en' ? 
          "Hello! 👋 Welcome to Gjenerali SHPK Construction! I'm here to help with your construction needs. What can I assist you with today?" :
          "Përshëndetje! 👋 Mirë se vini në Gjenerali SHPK Construction! Jam këtu për t'ju ndihmuar me nevojat tuaja të ndërtimit. Si mund t'ju ndihmoj sot?",
        actions: [
          { text: currentLanguage === 'en' ? "💰 Get Pricing Info" : "💰 Informacion Çmimesh", action: "pricing" },
          { text: currentLanguage === 'en' ? "🏗️ Our Services" : "🏗️ Shërbimet Tona", action: "services" },
          { text: currentLanguage === 'en' ? "📞 Contact Team" : "📞 Kontaktoni Ekipin", action: "contact" },
          { text: currentLanguage === 'en' ? "📸 View Projects" : "📸 Shikoni Projektet", action: "gallery" }
        ]
      };
    }
    
    if (msg.includes('thank') || msg.includes('thanks') || msg.includes('faleminderit') || msg.includes('rrofsh')) {
      return {
        text: currentLanguage === 'en' ? 
          "You're very welcome! 😊 Is there anything else I can help you with regarding your construction project?" :
          "Shumë mirë se erdhët! 😊 Ka diçka tjetër që mund t'ju ndihmoj lidhur me projektin tuaj të ndërtimit?",
        actions: [
          { text: currentLanguage === 'en' ? "📞 Contact Our Team" : "📞 Kontaktoni Ekipin", action: "contact" },
          { text: currentLanguage === 'en' ? "📊 Get Quote" : "📊 Merrni Ofertë", action: "calculator" },
          { text: currentLanguage === 'en' ? "❓ Ask Another Question" : "❓ Bëni Pyetje Tjetër", action: "help" }
        ]
      };
    }
    
    if (msg.includes('gallery') || msg.includes('photos') || msg.includes('pictures') || msg.includes('work') ||
        msg.includes('galeri') || msg.includes('foto') || msg.includes('punë')) {
      return {
        text: currentLanguage === 'en' ? 
          "Great! You can see our completed projects and quality work in our gallery. We have regular photos and amazing before/after transformations!" :
          "Shkëlqyer! Mund të shikoni projektet tona të përfunduara dhe punën cilësore në galerinë tonë. Kemi foto të rregullta dhe transformime të mahnitshme para/pas!",
        actions: [
          { text: currentLanguage === 'en' ? "📸 View Gallery" : "📸 Shikoni Galerinë", action: "gallery" },
          { text: currentLanguage === 'en' ? "🔄 Before/After Projects" : "🔄 Projekte Para/Pas", action: "before-after" },
          { text: currentLanguage === 'en' ? "📞 Discuss Your Project" : "📞 Diskutoni Projektin", action: "contact" }
        ]
      };
    }
    
    return {
      text: currentLanguage === 'en' ? 
        "I'd love to help you with that! 🤔 I'm here to answer questions about our construction services, pricing, projects, and more. Here are some popular topics I can help with:" :
        "Do të doja shumë t'ju ndihmoja me këtë! 🤔 Jam këtu për t'u përgjigjur pyetjeve për shërbimet tona të ndërtimit, çmimet, projektet dhe më shumë. Këtu janë disa tema të njohura me të cilat mund t'ju ndihmoj:",
      actions: [
        { text: currentLanguage === 'en' ? "💰 Pricing & Quotes" : "💰 Çmime & Oferta", action: "pricing" },
        { text: currentLanguage === 'en' ? "🏗️ Our Services" : "🏗️ Shërbimet Tona", action: "services" },
        { text: currentLanguage === 'en' ? "📞 Contact Team" : "📞 Kontaktoni Ekipin", action: "contact" },
        { text: currentLanguage === 'en' ? "📸 View Our Work" : "📸 Shikoni Punën Tonë", action: "gallery" }
      ]
    };
  };

  const handleChatAction = (action) => {
    switch (action) {
      case 'calculator':
        setActiveTab('calculator');
        setChatOpen(false);
        break;
      case 'contact':
        setActiveTab('contact');
        setChatOpen(false);
        break;
      case 'gallery':
        setActiveTab('gallery');
        setChatOpen(false);
        break;
      case 'before-after':
        setActiveTab('gallery');
        setSelectedCategory('before-after');
        setChatOpen(false);
        break;
      case 'call':
        window.open(`tel:${companyInfo.phone}`);
        break;
      case 'email':
        window.open(`mailto:${companyInfo.email}?subject=Construction Project Inquiry`);
        break;
      case 'whatsapp':
        window.open(`https://wa.me/${companyInfo.phone.replace(/[^0-9]/g, '')}?text=Hello! I'm interested in your construction services.`);
        break;
      case 'address':
        const botMessage = {
          id: Date.now(),
          text: `📍 Our Office Location:\n${companyInfo.address}\n\nWe'd love to meet you in person to discuss your project!`,
          sender: 'bot',
          time: new Date(),
          actions: [
            { text: "📞 Call Before Visit", action: "call" },
            { text: "📧 Schedule Meeting", action: "email" }
          ]
        };
        setChatMessages(prev => [...prev, botMessage]);
        break;
      default:
        // Trigger relevant responses
        if (action === 'pricing') {
          setActiveTab('calculator');
          setChatOpen(false);
        } else if (action === 'services') {
          setActiveTab('home');
          setChatOpen(false);
          // Scroll to services section after a brief delay
          setTimeout(() => {
            const servicesSection = document.querySelector('.services-preview');
            if (servicesSection) {
              servicesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 100);
        }
        break;
    }
  };

  const handleChatSubmit = (e) => {
    e.preventDefault();
    if (!chatInput.trim()) return;

    const userMessage = {
      id: Date.now(),
      text: chatInput,
      sender: 'user',
      time: new Date()
    };

    setChatMessages(prev => [...prev, userMessage]);
    const currentInput = chatInput;
    setChatInput('');

    // Bot response after delay
    setTimeout(() => {
      const response = getBotResponse(currentInput);
      const botMessage = {
        id: Date.now() + 1,
        text: response.text || response,
        sender: 'bot',
        time: new Date(),
        actions: response.actions || null
      };
      setChatMessages(prev => [...prev, botMessage]);
    }, 1000);
  };

  return (
    <div className="App">
      <header className="header">
        <div className="header-particles">
          <div className="header-particle particle-1">⚡</div>
          <div className="header-particle particle-2">🔧</div>
          <div className="header-particle particle-3">🏗️</div>
          <div className="header-particle particle-4">⭐</div>
        </div>
        
        <div className="header-content">
          <div className="logo-section">
            <img src="/logo.png" alt="Gjenerali SHPK Construction Logo" className="company-logo" />
            <h1>{companyInfo.name || 'Gjenerali SHPK Construction'}</h1>
          </div>
          
          {/* Mobile Menu Button */}
          <button 
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <span className={`hamburger ${mobileMenuOpen ? 'active' : ''}`}>
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>
        </div>
        
        <nav className={`nav ${mobileMenuOpen ? 'nav-open' : ''}`}>
          <button onClick={() => { navigateToTab('home'); setMobileMenuOpen(false); }} className={activeTab === 'home' ? 'active' : ''}>
            {t('home')}
          </button>
          <button onClick={() => { navigateToTab('about'); setMobileMenuOpen(false); }} className={activeTab === 'about' ? 'active' : ''}>
            {t('about')}
          </button>
          <button onClick={() => { navigateToTab('projects'); setMobileMenuOpen(false); }} className={activeTab === 'projects' ? 'active' : ''}>
            {t('projects')}
          </button>
          <button onClick={() => { navigateToTab('gallery'); setMobileMenuOpen(false); }} className={activeTab === 'gallery' ? 'active' : ''}>
            {t('gallery')}
          </button>
          <button onClick={() => { navigateToTab('calculator'); setMobileMenuOpen(false); }} className={activeTab === 'calculator' ? 'active' : ''}>
            {t('calculator')}
          </button>
          <button onClick={() => { navigateToTab('blog'); setMobileMenuOpen(false); }} className={activeTab === 'blog' ? 'active' : ''}>
            {t('blog')}
          </button>
          <button onClick={() => { navigateToTab('contact'); setMobileMenuOpen(false); }} className={activeTab === 'contact' ? 'active' : ''}>
            {t('contact')}
          </button>
          <button onClick={() => { navigateToTab('partners'); setMobileMenuOpen(false); }} className={activeTab === 'partners' ? 'active' : ''}>
            Partners
          </button>
          <button onClick={() => { navigateToTab('admin'); setMobileMenuOpen(false); }} className={activeTab === 'admin' ? 'active' : ''}>
            Admin
          </button>
          
          <div className="language-switcher">
            <button onClick={() => changeLanguage('en')} className={i18n.currentLanguage === 'en' ? 'active' : ''}>EN</button>
            <button onClick={() => changeLanguage('sq')} className={i18n.currentLanguage === 'sq' ? 'active' : ''}>SQ</button>
          </div>
        </nav>
      </header>

      {/* Mobile Menu Button - Outside Header */}
      <button 
        className="mobile-menu-btn-floating"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        aria-label="Toggle menu"
      >
        <span className={`hamburger ${mobileMenuOpen ? 'active' : ''}`}>
          <span></span>
          <span></span>
          <span></span>
        </span>
      </button>

      {/* Mobile Side Menu - Outside Header */}
      <nav className={`mobile-side-menu ${mobileMenuOpen ? 'nav-open' : ''}`}>
        <button onClick={() => { navigateToTab('home'); setMobileMenuOpen(false); }} className={activeTab === 'home' ? 'active' : ''}>
          {t('home')}
        </button>
        <button onClick={() => { navigateToTab('about'); setMobileMenuOpen(false); }} className={activeTab === 'about' ? 'active' : ''}>
          {t('about')}
        </button>
        <button onClick={() => { navigateToTab('projects'); setMobileMenuOpen(false); }} className={activeTab === 'projects' ? 'active' : ''}>
          {t('projects')}
        </button>
        <button onClick={() => { navigateToTab('gallery'); setMobileMenuOpen(false); }} className={activeTab === 'gallery' ? 'active' : ''}>
          {t('gallery')}
        </button>
        <button onClick={() => { navigateToTab('calculator'); setMobileMenuOpen(false); }} className={activeTab === 'calculator' ? 'active' : ''}>
          {t('calculator')}
        </button>
        <button onClick={() => { navigateToTab('blog'); setMobileMenuOpen(false); }} className={activeTab === 'blog' ? 'active' : ''}>
          {t('blog')}
        </button>
        <button onClick={() => { navigateToTab('contact'); setMobileMenuOpen(false); }} className={activeTab === 'contact' ? 'active' : ''}>
          {t('contact')}
        </button>
        <button onClick={() => { navigateToTab('partners'); setMobileMenuOpen(false); }} className={activeTab === 'partners' ? 'active' : ''}>
          Partners
        </button>
        <button onClick={() => { navigateToTab('admin'); setMobileMenuOpen(false); }} className={activeTab === 'admin' ? 'active' : ''}>
          Admin
        </button>
        
        <div className="language-switcher">
          <button onClick={() => changeLanguage('en')} className={i18n.currentLanguage === 'en' ? 'active' : ''}>EN</button>
          <button onClick={() => changeLanguage('sq')} className={i18n.currentLanguage === 'sq' ? 'active' : ''}>SQ</button>
        </div>
      </nav>

      <main className="main-content">
        {activeTab === 'home' && (
          <div className="tab-content">
            <section className="hero">
              <div className="construction-elements">
                <div className="crane crane-1">🏗️</div>
                <div className="crane crane-2">🏗️</div>
                <div className="building building-1">🏢</div>
                <div className="building building-2">🏠</div>
                <div className="tools tools-1">🔨</div>
                <div className="tools tools-2">🔧</div>
                <div className="blueprint">📐</div>
                <div className="helmet">⛑️</div>
              </div>
              <div className="floating-particles">
                <div className="particle particle-1">🔨</div>
                <div className="particle particle-2">🏗️</div>
                <div className="particle particle-3">🧱</div>
                <div className="particle particle-4">⚡</div>
                <div className="particle particle-5">🏠</div>
                <div className="particle particle-6">🔧</div>
              </div>
              <div className="hero-content">
                <div className="hero-logo-container">
                  <img src="/logo.png" alt="Gjenerali SHPK Construction" className="hero-logo" />
                  <div className="logo-glow"></div>
                </div>
                <h1>{t('heroTitle')}</h1>
                <div className="hero-buttons">
                  <button className="cta-primary" onClick={() => setActiveTab('contact')}>{t('getQuote')}</button>
                  <button className="cta-secondary" onClick={() => setActiveTab('projects')}>{t('viewProjects')}</button>
                </div>
                
                <div className="stats-section">
                  <div className="stat-item">
                    <div className="stat-number">{counters.experience}+</div>
                    <div className="stat-label">{t('yearsExperience')}</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-number">{counters.projects}+</div>
                    <div className="stat-label">{t('projectsCompleted')}</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-number">{counters.clients}+</div>
                    <div className="stat-label">{t('happyClients')}</div>
                  </div>
                </div>
              </div>
            </section>

            <section className="services-preview scroll-section animate-on-scroll">
              <h2>{t('ourServices')}</h2>
              <div className="services-grid">
                <div className="service-card">
                  <div className="service-icon">🏠</div>
                  <h3>{t('residential')}</h3>
                  <p>{t('residentialDesc')}</p>
                </div>
                <div className="service-card">
                  <div className="service-icon">🏢</div>
                  <h3>{t('commercial')}</h3>
                  <p>{t('commercialDesc')}</p>
                </div>
                <div className="service-card">
                  <div className="service-icon">🔨</div>
                  <h3>{t('renovation')}</h3>
                  <p>{t('renovationDesc')}</p>
                </div>
                <div className="service-card">
                  <div className="service-icon">📋</div>
                  <h3>{t('projectManagement')}</h3>
                  <p>{t('projectManagementDesc')}</p>
                </div>
              </div>
            </section>

            <section className="contact-preview scroll-section animate-on-scroll">
              <h2>{t('readyToStart')}</h2>
              <div className="contact-info-grid">
                <div className="contact-item" onClick={() => window.open(`https://wa.me/355682091156?text=${encodeURIComponent('Përshëndetje, dëshiroj informacion për shërbimet tuaja të ndërtimit.')}`, '_blank')} style={{cursor: 'pointer'}}>
                  <span className="contact-icon">📞</span>
                  <div>
                    <h4>{t('callUs')}</h4>
                    <p>{companyInfo.phone}</p>
                  </div>
                </div>
                <div className="contact-item" onClick={() => window.open('mailto:altion.basha@gjenerali.al', '_blank')} style={{cursor: 'pointer'}}>
                  <span className="contact-icon">✉️</span>
                  <div>
                    <h4>{t('emailUs')}</h4>
                    <p>{companyInfo.email}</p>
                  </div>
                </div>
                <div className="contact-item" onClick={() => window.open('https://www.google.com/maps/search/?api=1&query=Altion+Basha+Rade+Manze+Durres+Albania', '_blank')} style={{cursor: 'pointer'}}>
                  <span className="contact-icon">📍</span>
                  <div>
                    <h4>{t('visitUs')}</h4>
                    <p>{companyInfo.address}</p>
                  </div>
                </div>
              </div>
            </section>

            <section className="testimonials scroll-section animate-on-scroll">
              <h2>{t('testimonials')}</h2>
              <div className="testimonials-grid">
                {testimonials.map(testimonial => (
                  <div key={testimonial.id} className="testimonial-card">
                    <div className="stars">
                      {'⭐'.repeat(testimonial.rating)}
                    </div>
                    <p>"{testimonial.text}"</p>
                    <h4>- {testimonial.name}</h4>
                  </div>
                ))}
              </div>
            </section>

            {/* Gallery Preview Section */}
            <section className="gallery-preview">
              <h2>{t('ourWork')}</h2>
              <p>{t('galleryPreviewDesc')}</p>
              <div className="gallery-preview-grid">
                {(beforeAfterProjects && beforeAfterProjects.length > 0) ? (
                  beforeAfterProjects.slice(0, 6).map(project => (
                    <div key={project.id} className="gallery-preview-item" onClick={() => setLightboxImage(`/uploads/${project.afterImage}`)}>
                      <img src={`/uploads/${project.afterImage}`} alt={project.title} />
                      <div className="gallery-overlay">
                        <span>🔍</span>
                      </div>
                    </div>
                  ))
                ) : (
                  // Static demo images - always show when no beforeAfterProjects
                  [
                    'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
                    'https://images.unsplash.com/photo-1504307651254-35680f356dfd?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
                    'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
                    'https://images.unsplash.com/photo-1448630360428-65456885c650?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
                    'https://images.unsplash.com/photo-1503387762-592deb58ef4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
                    'https://images.unsplash.com/photo-1581094794329-c8112a89af12?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
                  ].map((imageUrl, index) => (
                    <div key={`static-${index}`} className="gallery-preview-item" onClick={() => {
                      setLightboxImage(imageUrl);
                    }}>
                      <img 
                        src={imageUrl} 
                        alt={`Construction Project ${index + 1}`}
                        loading="lazy"
                      />
                      <div className="gallery-overlay">
                        <span>🔍</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
              <button className="cta-primary" onClick={() => {
                console.log('View All Projects clicked');
                navigateToTab('gallery');
              }}>
                {t('viewAllProjects')}
              </button>
            </section>
          </div>
        )}

        {/* Job Application Modal */}
        {selectedJob && (
          <div className="modal-overlay" onClick={() => setSelectedJob(null)}>
            <div className="modal-content job-modal" onClick={(e) => e.stopPropagation()}>
              <button className="modal-close" onClick={() => setSelectedJob(null)}>×</button>
              
              <div className="job-details">
                <h2>{selectedJob.title}</h2>
                <div className="job-meta">
                  <span className="job-type">{selectedJob.type}</span>
                  {selectedJob.location && <span>📍 {selectedJob.location}</span>}
                  {selectedJob.salary && <span>💰 {selectedJob.salary}</span>}
                </div>
                
                <div className="job-section">
                  <h3>Description</h3>
                  <p>{selectedJob.description}</p>
                </div>
                
                {selectedJob.requirements && (
                  <div className="job-section">
                    <h3>Requirements</h3>
                    <p>{selectedJob.requirements}</p>
                  </div>
                )}
                
                <div className="job-section">
                  <h3>Apply for this Position</h3>
                  <form onSubmit={handleJobApplicationSubmit} className="job-application-form">
                    <input
                      type="text"
                      placeholder="Your Name *"
                      value={jobApplicationForm.name}
                      onChange={(e) => setJobApplicationForm({...jobApplicationForm, name: e.target.value})}
                      required
                    />
                    
                    <input
                      type="email"
                      placeholder="Your Email *"
                      value={jobApplicationForm.email}
                      onChange={(e) => setJobApplicationForm({...jobApplicationForm, email: e.target.value})}
                      required
                    />
                    
                    <input
                      type="tel"
                      placeholder="Phone Number"
                      value={jobApplicationForm.phone}
                      onChange={(e) => setJobApplicationForm({...jobApplicationForm, phone: e.target.value})}
                    />
                    
                    <textarea
                      placeholder="Why are you interested in this position?"
                      value={jobApplicationForm.message}
                      onChange={(e) => setJobApplicationForm({...jobApplicationForm, message: e.target.value})}
                      rows="4"
                    />
                    
                    <div className="file-upload-group">
                      <label>
                        📄 Upload CV (optional)
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={(e) => setJobApplicationForm({...jobApplicationForm, cv: e.target.files[0]})}
                        />
                        {jobApplicationForm.cv && <span className="file-name">{jobApplicationForm.cv.name}</span>}
                      </label>
                      
                      <label>
                        📝 Upload Cover Letter (optional)
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={(e) => setJobApplicationForm({...jobApplicationForm, coverLetter: e.target.files[0]})}
                        />
                        {jobApplicationForm.coverLetter && <span className="file-name">{jobApplicationForm.coverLetter.name}</span>}
                      </label>
                    </div>
                    
                    <button type="submit" className="submit-application-btn">Submit Application</button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'about' && (
          <div className="tab-content">
            <h2>{(aboutContent.title && aboutContent.title.trim()) ? aboutContent.title : t('aboutTitle')}</h2>
            <p>{(aboutContent.description && aboutContent.description.trim()) ? aboutContent.description : t('aboutDescription')}</p>
            
            {aboutContent.mission && aboutContent.mission.trim() && (
              <div className="mission-section">
                <h3>{t('ourMission')}</h3>
                <p>{aboutContent.mission}</p>
              </div>
            )}
            
            {aboutContent.vision && aboutContent.vision.trim() && (
              <div className="vision-section">
                <h3>{t('ourVision')}</h3>
                <p>{aboutContent.vision}</p>
              </div>
            )}
            
            <h3>{t('ourServices')}</h3>
            <ul>
              {aboutContent.services && aboutContent.services.length > 0 ? (
                aboutContent.services.map((service, index) => (
                  <li key={index}>{service}</li>
                ))
              ) : (
                <>
                  <li>{t('residentialConstruction')}</li>
                  <li>{t('commercialBuilding')}</li>
                  <li>{t('renovationsRemodeling')}</li>
                  <li>{t('projectManagement')}</li>
                </>
              )}
            </ul>
          </div>
        )}

        {activeTab === 'projects' && (
          <div className="tab-content">
            <h2>Our Projects</h2>
            
            <div className="projects-header">
              <button 
                className="gallery-link-btn"
                onClick={() => setActiveTab('gallery')}
              >
                📸 View Full Gallery
              </button>
            </div>

            {/* Show Projects from Admin Panel */}
            <div className="projects-grid">
              {projects.map(project => (
                <div key={project.id} className="project-card">
                  <div className="project-header">
                    <h3>{project.title}</h3>
                    <span className={`project-status status-${project.status}`}>
                      {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
                    </span>
                  </div>
                  
                  {project.images && project.images.length > 0 && (
                    <div className="project-images">
                      {project.images.map((image, index) => (
                        <img 
                          key={index}
                          src={image} 
                          alt={`${project.title} ${index + 1}`}
                          onClick={() => setLightboxImage(image)}
                          style={{
                            width: project.images.length === 1 ? '100%' : '48%',
                            height: '200px',
                            objectFit: 'cover',
                            margin: '2px',
                            cursor: 'pointer',
                            borderRadius: '8px'
                          }}
                        />
                      ))}
                    </div>
                  )}
                  
                  <div className="project-content">
                    <p>{project.description}</p>
                    <div className="project-details">
                      <span className="project-category">📋 {project.category}</span>
                      {project.startDate && (
                        <span className="project-date">📅 Started: {new Date(project.startDate).toLocaleDateString()}</span>
                      )}
                      {project.endDate && (
                        <span className="project-date">🏁 Completed: {new Date(project.endDate).toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="project-status-grid">
              {projectStatus.map(status => (
                <div key={status.id} className="status-card">
                  <div className="status-header">
                    <h3>{status.title}</h3>
                    <span className="status-date">Updated: {new Date(status.date).toLocaleDateString()}</span>
                  </div>
                  
                  <div className="status-content">
                    <p>{status.description}</p>
                    
                    {/* Show recent project images */}
                    <div className="status-images">
                      {files.slice(0, 3).map(file => (
                        file.originalname.match(/\.(jpg|jpeg|png|gif)$/i) && (
                          <div key={file.id} className="status-image">
                            <img 
                              src={`/${file.filename}`} 
                              alt={file.originalname}
                              onClick={() => setLightboxImage(`/${file.filename}`)}
                            />
                          </div>
                        )
                      ))}
                      {files.length > 3 && (
                        <div className="more-images" onClick={() => setActiveTab('gallery')}>
                          <span>+{files.length - 3} more</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="status-actions">
                    <button 
                      className="view-gallery-btn"
                      onClick={() => setActiveTab('gallery')}
                    >
                      View All Photos
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {projectStatus.length === 0 && projects.length === 0 && (
              <div className="empty-state">
                <div className="empty-icon">📋</div>
                <h3>No Project Updates Yet</h3>
                <p>Project status updates will appear here once added by the admin.</p>
                <button 
                  className="cta-secondary"
                  onClick={() => setActiveTab('gallery')}
                >
                  Browse Our Gallery
                </button>
              </div>
            )}

            {lightboxImage && (
              <div className="lightbox" onClick={() => setLightboxImage(null)}>
                <div className="lightbox-content">
                  <span className="close">&times;</span>
                  <img src={lightboxImage} alt="Project" />
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'gallery' && (
          <div className="tab-content">
            <h2>{t('projectGallery')}</h2>
            
            <div className="gallery-tabs">
              <button 
                className={selectedCategory === 'all' ? 'active' : ''} 
                onClick={() => setSelectedCategory('all')}
              >
                {t('allProjects')}
              </button>
              <button 
                className={selectedCategory === 'before-after' ? 'active' : ''} 
                onClick={() => setSelectedCategory('before-after')}
              >
                {t('beforeAfter')}
              </button>
              <button 
                className={selectedCategory === 'residential' ? 'active' : ''} 
                onClick={() => setSelectedCategory('residential')}
              >
                {t('residential')}
              </button>
              <button 
                className={selectedCategory === 'commercial' ? 'active' : ''} 
                onClick={() => setSelectedCategory('commercial')}
              >
                {t('commercial')}
              </button>
            </div>

            {selectedCategory === 'before-after' ? (
              <div className="before-after-section">
                {Array.isArray(beforeAfterProjects) && beforeAfterProjects.length > 0 ? (
                  <div className="portfolio-slider">
                    <div className="slider-container"
                      onTouchStart={handleTouchStart}
                      onTouchMove={handleTouchMove}
                      onTouchEnd={handleTouchEnd}
                    >
                      <div className="slider-track" style={{ transform: `translateX(-${currentSlide * 100}%)` }}>
                        {beforeAfterProjects.map((project, index) => (
                          <div key={project.id} className="slide">
                            <h3>{project.title}</h3>
                            {isAdminLoggedIn && (
                              <button 
                                className="admin-delete-btn"
                                onClick={() => deleteBeforeAfterProject(project.id)}
                                style={{
                                  position: 'absolute',
                                  top: '10px',
                                  right: '10px',
                                  background: '#dc2626',
                                  color: 'white',
                                  border: 'none',
                                  padding: '8px 16px',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  zIndex: 10,
                                  fontSize: '14px',
                                  fontWeight: 'bold'
                                }}
                              >
                                🗑️ Delete
                              </button>
                            )}
                            <div className="comparison-container">
                              <div className="before-after-slider">
                                <div className="before">
                                  <h4>Before</h4>
                                  <div className="project-image">
                                    <img 
                                      src={`/uploads/${project.beforeImage}`} 
                                      alt={`${project.title} - Before`}
                                      onClick={() => setLightboxImage(`/uploads/${project.beforeImage}`)}
                                    />
                                  </div>
                                </div>
                                <div className="slider-divider"></div>
                                <div className="after">
                                  <h4>After</h4>
                                  <div className="project-image">
                                    <img 
                                      src={`/uploads/${project.afterImage}`} 
                                      alt={`${project.title} - After`}
                                      onClick={() => setLightboxImage(`/uploads/${project.afterImage}`)}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div className="slider-controls">
                      <button 
                        className="slider-btn prev" 
                        onClick={() => setCurrentSlide(Math.max(0, currentSlide - 1))}
                        disabled={currentSlide === 0}
                      >
                        ‹
                      </button>
                      <div className="slider-dots">
                        {beforeAfterProjects.map((_, index) => (
                          <button
                            key={index}
                            className={`dot ${index === currentSlide ? 'active' : ''}`}
                            onClick={() => setCurrentSlide(index)}
                          />
                        ))}
                      </div>
                      <button 
                        className="slider-btn next" 
                        onClick={() => setCurrentSlide(Math.min(beforeAfterProjects.length - 1, currentSlide + 1))}
                        disabled={currentSlide === beforeAfterProjects.length - 1}
                      >
                        ›
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="empty-state">
                    <div className="empty-icon">🏗️</div>
                    <h3>No Before & After Projects Yet</h3>
                    <p>Before & After projects will appear here once uploaded by the admin.</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="gallery-projects-section">
                {/* Filter projects by category */}
                {projects
                  .filter(project => selectedCategory === 'all' || project.category === selectedCategory)
                  .map(project => (
                    <div key={project.id} className="gallery-project-group">
                      <div className="gallery-project-header">
                        <h3>{project.title}</h3>
                        <span className={`project-status status-${project.status}`}>
                          {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
                        </span>
                      </div>
                      
                      {project.images && project.images.length > 0 ? (
                        <div className="gallery-project-images">
                          {project.images.map((image, index) => (
                            <div key={index} className="gallery-image-item">
                              <img 
                                src={image} 
                                alt={`${project.title} ${index + 1}`}
                                onClick={() => setLightboxImage(image)}
                                loading="lazy"
                              />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p style={{ color: '#64748b', fontStyle: 'italic' }}>No images for this project</p>
                      )}
                    </div>
                  ))}
                
                {projects.filter(project => selectedCategory === 'all' || project.category === selectedCategory).length === 0 && (
                  <div className="empty-state">
                    <div className="empty-icon">🏗️</div>
                    <h3>No Projects in This Category</h3>
                    <p>Projects will appear here once added by the admin.</p>
                  </div>
                )}
              </div>
            )}

            {lightboxImage && (
              <div className="lightbox" onClick={() => setLightboxImage(null)}>
                <div className="lightbox-content">
                  <span className="close">&times;</span>
                  <img src={lightboxImage} alt="Gallery" />
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'contact' && (
          <div className="tab-content">
            <div className="contact-hero">
              <div className="video-background">
                <div className="video-placeholder">
                  🏗️ Construction in Progress
                </div>
                <div className="video-overlay">
                  <h2>{t('getFreeQuote')}</h2>
                  <p>{t('professionalServices')}</p>
                </div>
              </div>
            </div>
            
            <div className="contact-tabs">
              <button 
                className={activeContactTab === 'contact' ? 'active' : ''} 
                onClick={() => setActiveContactTab('contact')}
              >
                {t('contactUs')}
              </button>
              <button 
                className={activeContactTab === 'quote' ? 'active' : ''} 
                onClick={() => setActiveContactTab('quote')}
              >
                {t('requestQuote')}
              </button>
            </div>

            <div className="contact-section">
              <div className="form-container">
                {activeContactTab === 'contact' ? (
                  <form onSubmit={handleContactSubmit} className="contact-form enhanced-form">
                    <h3>{t('sendUsMessage')}</h3>
                    
                    {contactSuccess && (
                      <div style={{
                        padding: '12px 16px',
                        marginBottom: '16px',
                        backgroundColor: '#dcfce7',
                        border: '1px solid #86efac',
                        borderRadius: '8px',
                        color: '#166534',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <span style={{ fontSize: '20px' }}>✅</span>
                        <span>{t('messageSentSuccess') || 'Message sent successfully!'}</span>
                      </div>
                    )}
                    
                    {contactError && (
                      <div style={{
                        padding: '12px 16px',
                        marginBottom: '16px',
                        backgroundColor: '#fee2e2',
                        border: '1px solid #fca5a5',
                        borderRadius: '8px',
                        color: '#991b1b',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <span style={{ fontSize: '20px' }}>❌</span>
                        <span>{contactError}</span>
                      </div>
                    )}
                    
                    <div className="form-group">
                      <input
                        type="text"
                        placeholder=" "
                        value={contactForm.name}
                        onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                        required
                        id="contact-name"
                      />
                      <label htmlFor="contact-name">{t('yourName')}</label>
                    </div>
                    <div className="form-group">
                      <input
                        type="email"
                        placeholder=" "
                        value={contactForm.email}
                        onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                        required
                        id="contact-email"
                      />
                      <label htmlFor="contact-email">{t('yourEmail')}</label>
                    </div>
                    <div className="form-group">
                      <textarea
                        placeholder=" "
                        value={contactForm.message}
                        onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                        required
                        id="contact-message"
                        rows="4"
                      ></textarea>
                      <label htmlFor="contact-message">{t('yourMessage')}</label>
                    </div>
                    <button type="submit" className="submit-btn">
                      <span>{t('sendMessage')}</span>
                      <span className="btn-icon">📤</span>
                    </button>
                  </form>
                ) : (
                  <form onSubmit={handleQuoteSubmit} className="contact-form enhanced-form">
                    <h3>{t('requestQuote')}</h3>
                    
                    {quoteSuccess && (
                      <div style={{
                        padding: '12px 16px',
                        marginBottom: '16px',
                        backgroundColor: '#dcfce7',
                        border: '1px solid #86efac',
                        borderRadius: '8px',
                        color: '#166534',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <span style={{ fontSize: '20px' }}>✅</span>
                        <span>{t('quoteSentSuccess') || 'Quote request sent successfully!'}</span>
                      </div>
                    )}
                    
                    {quoteError && (
                      <div style={{
                        padding: '12px 16px',
                        marginBottom: '16px',
                        backgroundColor: '#fee2e2',
                        border: '1px solid #fca5a5',
                        borderRadius: '8px',
                        color: '#991b1b',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <span style={{ fontSize: '20px' }}>❌</span>
                        <span>{quoteError}</span>
                      </div>
                    )}
                    
                    <div className="form-row">
                      <div className="form-group">
                        <input
                          type="text"
                          placeholder=" "
                          value={quoteForm.name}
                          onChange={(e) => setQuoteForm({...quoteForm, name: e.target.value})}
                          required
                          id="quote-name"
                        />
                        <label htmlFor="quote-name">{t('yourName')}</label>
                      </div>
                      <div className="form-group">
                        <input
                          type="email"
                          placeholder=" "
                          value={quoteForm.email}
                          onChange={(e) => setQuoteForm({...quoteForm, email: e.target.value})}
                          required
                          id="quote-email"
                        />
                        <label htmlFor="quote-email">{t('yourEmail')}</label>
                      </div>
                    </div>
                    <div className="form-group">
                      <input
                        type="tel"
                        placeholder=" "
                        value={quoteForm.phone}
                        onChange={(e) => setQuoteForm({...quoteForm, phone: e.target.value})}
                        required
                        id="quote-phone"
                      />
                      <label htmlFor="quote-phone">{t('yourPhone')}</label>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <select
                          value={quoteForm.projectType}
                          onChange={(e) => setQuoteForm({...quoteForm, projectType: e.target.value})}
                          required
                          id="quote-type"
                        >
                          <option value="">{t('selectProjectType')}</option>
                          <option value="residential">{t('residential')}</option>
                          <option value="commercial">{t('commercial')}</option>
                          <option value="renovation">{t('renovation')}</option>
                        </select>
                        <label htmlFor="quote-type">{t('projectType')}</label>
                      </div>
                      <div className="form-group">
                        <select
                          value={quoteForm.budget}
                          onChange={(e) => setQuoteForm({...quoteForm, budget: e.target.value})}
                          required
                          id="quote-budget"
                        >
                          <option value="">{t('selectBudgetRange')}</option>
                          <option value="under-10k">{t('under10k')}</option>
                          <option value="10k-25k">{t('budget10k25k')}</option>
                          <option value="25k-50k">{t('budget25k50k')}</option>
                          <option value="over-50k">{t('over50k')}</option>
                        </select>
                        <label htmlFor="quote-budget">{t('budgetRange')}</label>
                      </div>
                    </div>
                    <div className="form-group">
                      <textarea
                        placeholder=" "
                        value={quoteForm.description}
                        onChange={(e) => setQuoteForm({...quoteForm, description: e.target.value})}
                        required
                        id="quote-description"
                        rows="4"
                      ></textarea>
                      <label htmlFor="quote-description">{t('projectDescription')}</label>
                    </div>
                    <button type="submit" className="submit-btn">
                      <span>{t('requestQuote')}</span>
                      <span className="btn-icon">💰</span>
                    </button>
                  </form>
                )}
              </div>
              
              <div className="contact-info">
                <h3>{t('getInTouch')}</h3>
                <div className="contact-methods">
                  <div className="contact-method">
                    <span className="method-icon">📞</span>
                    <div className="method-content">
                      <h4>{t('callUs')}</h4>
                      <p style={{fontSize: '1.2rem', fontWeight: '600', color: '#ff6b35', userSelect: 'text'}}>{companyInfo.phone}</p>
                      <button onClick={() => window.open(`https://wa.me/355682091156?text=${encodeURIComponent('Përshëndetje, dëshiroj informacion për shërbimet tuaja të ndërtimit.')}`, '_blank')} className="contact-btn call-btn">
                        {t('callNow')}
                      </button>
                    </div>
                  </div>
                  
                  <div className="contact-method">
                    <span className="method-icon">✉️</span>
                    <div className="method-content">
                      <h4>{t('emailUs')}</h4>
                      <p style={{fontSize: '1.1rem', fontWeight: '600', color: '#ff6b35', userSelect: 'text', wordBreak: 'break-all'}}>{companyInfo.email}</p>
                      <button onClick={() => window.open(`mailto:${companyInfo.email}`)} className="contact-btn email-btn">
                        {t('sendEmail')}
                      </button>
                    </div>
                  </div>
                  
                  <div className="contact-method">
                    <span className="method-icon">💬</span>
                    <div className="method-content">
                      <h4>{t('whatsapp')}</h4>
                      <p style={{fontSize: '1.2rem', fontWeight: '600', color: '#25D366', userSelect: 'text'}}>{companyInfo.phone}</p>
                      <button onClick={openWhatsApp} className="contact-btn whatsapp-btn">
                        {t('chatNow')}
                      </button>
                    </div>
                  </div>
                </div>
                
                <div className="map-container">
                  <h4>{t('findUs')}</h4>
                  <div className="map-wrapper">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d10190.012737289957!2d19.590902551151!3d41.426995935179455!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x13502a5c0da78165%3A0xfc00091cf894b9d3!2zTWFuw6t6LCBBbGJhbmlh!5e1!3m2!1sen!2sse!4v1771886290995!5m2!1sen!2sse"
                      width="100%"
                      height="300"
                      style={{border: 0}}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Manëz, Albania"
                    ></iframe>
                    <div className="map-overlay">
                      <div className="location-info">
                        <h5>📍 {t('ourLocation')}</h5>
                        <p>{companyInfo.address}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'calculator' && (
          <div className="tab-content">
            <div className="calculator-section">
              <h2>{t('constructionCalculator')}</h2>
              <p>{t('calculatorInstantEstimate')}</p>
              
              <div className="calculator-form">
                <div className="form-group">
                  <label htmlFor="serviceType">{t('selectServiceType')}</label>
                  <select
                    id="serviceType"
                    className="calculator-select"
                    value={calculatorData.serviceId}
                    onChange={(e) => setCalculatorData({...calculatorData, serviceId: e.target.value})}
                  >
                    <option value="">{t('chooseService')}</option>
                    {calculatorServices.map(service => (
                      <option key={service.id} value={service.id}>
                        {service.serviceName} - {service.pricePerM2} EUR/m²
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="area">{t('areaSquareMeters')}</label>
                  <input
                    id="area"
                    className="calculator-input"
                    type="number"
                    placeholder={t('enterArea')}
                    value={calculatorData.area}
                    onChange={(e) => setCalculatorData({...calculatorData, area: e.target.value})}
                    min="1"
                  />
                </div>
                
                <button onClick={calculatePrice} className="calculate-btn">
                  {t('calculateEstimate')}
                </button>
                
                {estimatedPrice > 0 && selectedService && (
                  <div className="price-result">
                    <h3>{t('estimatedCost')}</h3>
                    <div className="price-breakdown">
                      <p><strong>{t('service')}:</strong> {selectedService.serviceName}</p>
                      <p><strong>{t('area')}:</strong> {calculatorData.area} m²</p>
                      <p><strong>{t('pricePerM2')}:</strong> {selectedService.pricePerM2} EUR</p>
                      <div className="calculation">
                        {calculatorData.area} m² × {selectedService.pricePerM2} EUR/m² = <strong>{estimatedPrice.toLocaleString()} EUR</strong>
                      </div>
                    </div>
                    <div className="price-amount">€{estimatedPrice.toLocaleString()}</div>
                    <p className="disclaimer">
                      {t('calculatorDisclaimer')}{' '}
                      <button 
                        onClick={() => navigateToTab('contact')} 
                        className="quote-link"
                      >
                        {t('quote')}
                      </button>.
                    </p>
                  </div>
                )}

                {calculatorServices.length === 0 && (
                  <div className="empty-state">
                    <p>{t('noServicesAvailable')}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'blog' && (
          <div className="tab-content">
            <h2>{t('constructionNews')}</h2>
            <div className="blog-grid">
              <article className="blog-post">
                <h3>{t('modernTrends')}</h3>
                <p className="blog-date">March 15, 2024</p>
                <p>{t('modernTrendsDesc')}</p>
                <a href="https://www.archdaily.com/tag/albania" target="_blank" rel="noopener noreferrer">
                  <button className="read-more">{t('readMore')}</button>
                </a>
              </article>
              
              <article className="blog-post">
                <h3>{t('dreamHomeTips')}</h3>
                <p className="blog-date">March 10, 2024</p>
                <p>{t('dreamHomeTipsDesc')}</p>
                <a href="https://www.houzz.com/photos/query/home-design" target="_blank" rel="noopener noreferrer">
                  <button className="read-more">{t('readMore')}</button>
                </a>
              </article>
              
              <article className="blog-post">
                <h3>{t('buildingRegulations')}</h3>
                <p className="blog-date">March 5, 2024</p>
                <p>{t('buildingRegulationsDesc')}</p>
                <a href="https://www.doingbusiness.org/en/data/exploreeconomies/albania" target="_blank" rel="noopener noreferrer">
                  <button className="read-more">{t('readMore')}</button>
                </a>
              </article>
            </div>
            
            <div className="social-media">
              <h3>Follow Us</h3>
              <div className="social-links">
                <a href="https://facebook.com/altionsconstruction" target="_blank" rel="noopener noreferrer">
                  📘 Facebook
                </a>
                <a href="https://instagram.com/altionsconstruction" target="_blank" rel="noopener noreferrer">
                  📷 Instagram
                </a>
                <a href="https://linkedin.com/company/altionsconstruction" target="_blank" rel="noopener noreferrer">
                  💼 LinkedIn
                </a>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'partners' && (
          <div className="tab-content">
            <Partners />
          </div>
        )}

        )}
      </main>

      {/* Chatbot Widget */}
      <div className={`chatbot-widget ${chatOpen ? 'open' : ''}`}>
        <div className="chat-toggle" onClick={() => setChatOpen(!chatOpen)}>
          {chatOpen ? '✕' : '💬'}
        </div>
        
        {chatOpen && (
          <div className="chat-container">
            <div className="chat-header">
              <div className="chat-header-content">
                <h4>🏗️ {t('chatHelp')}</h4>
                <span>{t('chatOnline')}</span>
              </div>
              <button className="chat-minimize" onClick={() => setChatOpen(false)}>
                ➖
              </button>
            </div>
            
            <div className="chat-messages">
              {chatMessages.map(message => (
                <div key={message.id} className={`message ${message.sender}`}>
                  <div className="message-content">
                    {message.text}
                  </div>
                  {message.actions && (
                    <div className="message-actions">
                      {message.actions.map((action, index) => (
                        <button
                          key={index}
                          className="action-btn"
                          onClick={() => handleChatAction(action.action)}
                        >
                          {action.text}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="message-time">
                    {message.time.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
            
            <form onSubmit={handleChatSubmit} className="chat-input-form">
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                placeholder={t('chatPlaceholder')}
                className="chat-input"
              />
              <button type="submit" className="chat-send">
                ➤
              </button>
            </form>
          </div>
        )}
      </div>
      
      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-section">
            <h3>{companyInfo.name || 'Gjenerali Construction and More'}</h3>
            <div className="social-links">
              <a href={`mailto:${companyInfo.email}`} className="social-link" aria-label="Email">📧</a>
              <a href={`tel:${companyInfo.phone}`} className="social-link" aria-label="Phone">📞</a>
              <a href={`https://maps.google.com/?q=${encodeURIComponent(companyInfo.address)}`} className="social-link" aria-label="Location" target="_blank" rel="noopener noreferrer">📍</a>
            </div>
            {(socialLinks.facebook || socialLinks.instagram || socialLinks.tiktok || socialLinks.linkedin || socialLinks.youtube || socialLinks.twitter) && (
              <div style={{ marginTop: '16px' }}>
                <h4 style={{ fontSize: '0.9rem', marginBottom: '12px', color: '#94a3b8' }}>Follow Us</h4>
                <div className="social-links">
                  {socialLinks.facebook && (
                    <a href={socialLinks.facebook} className="social-link" aria-label="Facebook" target="_blank" rel="noopener noreferrer">
                      <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                    </a>
                  )}
                  {socialLinks.instagram && (
                    <a href={socialLinks.instagram} className="social-link" aria-label="Instagram" target="_blank" rel="noopener noreferrer">
                      <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                    </a>
                  )}
                  {socialLinks.tiktok && (
                    <a href={socialLinks.tiktok} className="social-link" aria-label="TikTok" target="_blank" rel="noopener noreferrer">
                      <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
                    </a>
                  )}
                  {socialLinks.linkedin && (
                    <a href={socialLinks.linkedin} className="social-link" aria-label="LinkedIn" target="_blank" rel="noopener noreferrer">
                      <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                    </a>
                  )}
                  {socialLinks.youtube && (
                    <a href={socialLinks.youtube} className="social-link" aria-label="YouTube" target="_blank" rel="noopener noreferrer">
                      <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                    </a>
                  )}
                  {socialLinks.twitter && (
                    <a href={socialLinks.twitter} className="social-link" aria-label="Twitter" target="_blank" rel="noopener noreferrer">
                      <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
          
          <div className="footer-section">
            <h4>{t('quickLinks')}</h4>
            <ul>
              <li><button onClick={() => navigateToTab('home')}>{t('home')}</button></li>
              <li><button onClick={() => navigateToTab('about')}>{t('about')}</button></li>
              <li><button onClick={() => navigateToTab('projects')}>{t('projects')}</button></li>
              <li><button onClick={() => navigateToTab('gallery')}>{t('gallery')}</button></li>
              <li><button onClick={() => navigateToTab('contact')}>{t('contact')}</button></li>
              <li><button onClick={() => navigate('/about/work')}>💼 Work With Us</button></li>
            </ul>
          </div>
          
          <div className="footer-section">
            <h4>{t('services')}</h4>
            <ul>
              <li>{t('residentialConstruction')}</li>
              <li>{t('commercialConstruction')}</li>
              <li>{t('renovation')}</li>
              <li>{t('projectManagement')}</li>
            </ul>
          </div>
          
          <div className="footer-section">
            <h4>{t('contactInfo')}</h4>
            <div className="contact-details">
              <p>📞 {companyInfo.phone}</p>
              <p>📧 {companyInfo.email}</p>
              <p>📍 {companyInfo.address}</p>
            </div>
          </div>
        </div>
        
        <div className="footer-bottom">
          <p>&copy; 2024 Gjenerali SHPK Construction. {t('allRightsReserved')}</p>
          <p>
            {t('builtWith')} ❤️ {t('inAlbania')}
          </p>
          <div style={{ marginTop: '12px', display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="/privacy-policy" style={{ color: '#3b82f6', textDecoration: 'none' }}>
              {i18n.language === 'sq' ? 'Politika e Privatësisë' : 'Privacy Policy'}
            </a>
            <span style={{ color: '#64748b' }}>•</span>
            <a href="/terms-of-service" style={{ color: '#3b82f6', textDecoration: 'none' }}>
              {i18n.language === 'sq' ? 'Kushtet e Shërbimit' : 'Terms of Service'}
            </a>
            <span style={{ color: '#64748b' }}>•</span>
            <a href="/disclaimer" style={{ color: '#3b82f6', textDecoration: 'none' }}>
              {i18n.language === 'sq' ? 'Disclaimer' : 'Disclaimer'}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <Routes>
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy language="sq" />} />
        <Route path="/terms-of-service" element={<TermsOfService language="sq" />} />
        <Route path="/disclaimer" element={<Disclaimer language="sq" />} />
        <Route path="/job/:jobId" element={<JobApplication />} />
        <Route path="*" element={<AppContent />} />
      </Routes>
      <CookieConsent language="sq" />
    </Suspense>
  );
}

export default App;
